/**
 * Узкая сетка для ЕГЭ №18: числа и формулы МАКС/МИН/СУММ (и англ. MAX/MIN/SUM).
 * Без ВПР, фильтров и именованных диапазонов.
 */
import { useMemo } from 'react';
import { emptySpreadsheetState, useWorkspaceSync } from './useWorkspaceSync';

const COL_LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

function colLabel(c) {
  return COL_LETTERS[c] || `C${c + 1}`;
}

function parseCellRef(ref) {
  const m = String(ref || '').trim().toUpperCase().match(/^([A-Z]+)(\d+)$/);
  if (!m) return null;
  let col = 0;
  for (const ch of m[1]) col = col * 26 + (ch.charCodeAt(0) - 64);
  col -= 1;
  const row = Number(m[2]) - 1;
  if (row < 0 || col < 0) return null;
  return { row, col };
}

function expandRange(a, b) {
  const r0 = Math.min(a.row, b.row);
  const r1 = Math.max(a.row, b.row);
  const c0 = Math.min(a.col, b.col);
  const c1 = Math.max(a.col, b.col);
  const out = [];
  for (let r = r0; r <= r1; r += 1) {
    for (let c = c0; c <= c1; c += 1) out.push({ row: r, col: c });
  }
  return out;
}

function cellKey(r, c) {
  return `${r},${c}`;
}

function rawCell(state, r, c) {
  return String(state.cells?.[cellKey(r, c)] ?? '');
}

function parseArgs(argStr) {
  return String(argStr || '')
    .split(';')
    .flatMap((part) => part.split(','))
    .map((s) => s.trim())
    .filter(Boolean);
}

function collectNumbers(state, args, evalCell, visiting) {
  const nums = [];
  for (const arg of args) {
    if (/^[A-Z]+\d+:[A-Z]+\d+$/i.test(arg)) {
      const [left, right] = arg.split(':');
      const a = parseCellRef(left);
      const b = parseCellRef(right);
      if (!a || !b) continue;
      for (const ref of expandRange(a, b)) {
        const v = evalCell(ref.row, ref.col, visiting);
        if (typeof v === 'number' && Number.isFinite(v)) nums.push(v);
      }
      continue;
    }
    const ref = parseCellRef(arg);
    if (ref) {
      const v = evalCell(ref.row, ref.col, visiting);
      if (typeof v === 'number' && Number.isFinite(v)) nums.push(v);
      continue;
    }
    const n = Number(String(arg).replace(',', '.'));
    if (Number.isFinite(n)) nums.push(n);
  }
  return nums;
}

function evalFormula(state, formula, evalCell, visiting) {
  const body = formula.trim().replace(/^=/, '').trim();
  const m = body.match(/^(СУММ|SUM|МАКС|MAX|МИН|MIN)\s*\((.*)\)\s*$/i);
  if (!m) return '#ФОРМУЛА';
  const fn = m[1].toUpperCase();
  const nums = collectNumbers(state, parseArgs(m[2]), evalCell, visiting);
  if (!nums.length) return 0;
  if (fn === 'СУММ' || fn === 'SUM') return nums.reduce((a, b) => a + b, 0);
  if (fn === 'МАКС' || fn === 'MAX') return Math.max(...nums);
  if (fn === 'МИН' || fn === 'MIN') return Math.min(...nums);
  return '#ФОРМУЛА';
}

function makeEvaluator(state) {
  const cache = new Map();

  function evalCell(r, c, visiting = new Set()) {
    const key = cellKey(r, c);
    if (cache.has(key)) return cache.get(key);
    if (visiting.has(key)) return '#ЦИКЛ';
    const raw = rawCell(state, r, c).trim();
    if (!raw) {
      cache.set(key, null);
      return null;
    }
    if (raw.startsWith('=')) {
      visiting.add(key);
      const v = evalFormula(state, raw, evalCell, visiting);
      visiting.delete(key);
      cache.set(key, v);
      return v;
    }
    const n = Number(raw.replace(',', '.'));
    if (raw !== '' && Number.isFinite(n)) {
      cache.set(key, n);
      return n;
    }
    cache.set(key, raw);
    return raw;
  }

  return evalCell;
}

function displayValue(state, r, c, evalCell) {
  const raw = rawCell(state, r, c);
  if (!raw.startsWith('=')) return raw;
  const v = evalCell(r, c);
  if (v == null) return '';
  return String(v);
}

export default function SpreadsheetWorkspace({
  ydoc,
  workspaceConfig,
  seedState,
  dataKey = 'data',
  onStateChange,
  readOnly = false,
}) {
  const initial = useMemo(() => {
    const rows = Number(workspaceConfig?.rows) > 0 ? Number(workspaceConfig.rows) : 8;
    const cols = Number(workspaceConfig?.cols) > 0 ? Number(workspaceConfig.cols) : 6;
    const base = emptySpreadsheetState(rows, cols);
    if (workspaceConfig?.cells && typeof workspaceConfig.cells === 'object') {
      return { ...base, cells: { ...base.cells, ...workspaceConfig.cells } };
    }
    return base;
  }, [workspaceConfig]);

  const [state, setState] = useWorkspaceSync({
    ydoc,
    initialState: initial,
    seedState,
    dataKey,
    onChange: onStateChange,
  });

  const evalCell = useMemo(() => makeEvaluator(state), [state]);
  const rows = state.rows || 8;
  const cols = state.cols || 6;

  const setCell = (r, c, value) => {
    if (readOnly) return;
    setState((prev) => ({
      ...prev,
      cells: { ...(prev.cells || {}), [cellKey(r, c)]: value },
    }));
  };

  return (
    <div className="spreadsheet-workspace">
      <p className="ss-hint">
        Формулы: <code>=СУММ(A1:A5)</code>, <code>=МАКС(B1;B2)</code>, <code>=МИН(A1:C1)</code>
        {' '}(также SUM / MAX / MIN). Разделитель аргументов — точка с запятой или запятая.
      </p>
      <div className="ss-scroll">
        <table className="ss-table">
          <thead>
            <tr>
              <th />
              {Array.from({ length: cols }, (_, c) => (
                <th key={c}>{colLabel(c)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: rows }, (_, r) => (
              <tr key={r}>
                <th>{r + 1}</th>
                {Array.from({ length: cols }, (_, c) => {
                  const raw = rawCell(state, r, c);
                  const shown = displayValue(state, r, c, evalCell);
                  const isFormula = raw.startsWith('=');
                  return (
                    <td key={c} className={isFormula ? 'is-formula' : ''}>
                      <input
                        className="form-input ss-cell"
                        value={raw}
                        placeholder={isFormula ? shown : ''}
                        title={isFormula ? `= ${shown}` : undefined}
                        onChange={(e) => setCell(r, c, e.target.value)}
                        disabled={readOnly}
                        aria-label={`${colLabel(c)}${r + 1}`}
                      />
                      {isFormula && raw !== shown ? (
                        <span className="ss-preview" aria-hidden>{shown}</span>
                      ) : null}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
